<template>
    <div class="rd">
        <div class="pagina">
        <header class="paginakop">
            <div class="tekst">
                <h1>Site information</h1>
                <p>Everything about one location: opening hours, X-Cubes and prices.</p>
            </div>
        </header>

        <section v-if="locations.length > 1" class="paneel filters">
            <div class="filter-rij">
                <div class="veld f-loc">
                    <label for="si-loc">Location</label>
                    <select id="si-loc" v-model="selectedId">
                        <option v-for="loc in locations" :key="loc.id" :value="loc.id">{{ loc.name }}</option>
                    </select>
                </div>
            </div>
        </section>

        <p v-if="!loadingLocations && locations.length === 0" class="rd-laden">You have no locations.</p>
        <p v-else-if="loadingLocations" class="rd-laden">Loading locations...</p>

        <template v-if="selected">
            <section v-if="contentBlocks.length > 0" class="paneel operator-info">
                <div class="paneel-kop"><h2>Operator information</h2></div>
                <div class="cb-inhoud">
                    <render-content-blocks :content-blocks="contentBlocks" />
                </div>
            </section>

            <section class="rij rij-3">
                <!-- Location -->
                <div class="paneel">
                    <div class="paneel-kop"><h2>Location</h2></div>
                    <dl class="dl">
                        <div>
                            <dt>Venue</dt>
                            <dd>{{ selected.name }}</dd>
                        </div>
                        <div>
                            <dt>Address</dt>
                            <dd>
                                <template v-if="addressLine1 || selected.place_name">
                                    <template v-if="addressLine1">{{ addressLine1 }}<br></template>{{ addressLine2 }}
                                </template>
                                <span v-else class="leegwaarde">Not set</span>
                            </dd>
                        </div>
                        <div v-if="phone">
                            <dt>Phone</dt>
                            <dd>{{ phone }}</dd>
                        </div>
                        <div v-if="selected.email">
                            <dt>Email</dt>
                            <dd>
                                {{ selected.email }}
                                <a
                                    :href="`mailto:${selected.email}`"
                                    class="dd-ico"
                                    :title="`Email ${selected.email}`"
                                    :aria-label="`Email ${selected.email}`"
                                >
                                    <font-awesome-icon :icon="['fas', 'envelope']" />
                                </a>
                            </dd>
                        </div>
                        <div v-if="selected.public_booking_url">
                            <dt>Booking page</dt>
                            <dd class="breek">
                                {{ selected.public_booking_url }}
                                <a
                                    :href="selected.public_booking_url"
                                    target="_blank"
                                    rel="noopener"
                                    class="dd-ico"
                                    title="Open booking page"
                                    aria-label="Open booking page"
                                >
                                    <font-awesome-icon :icon="['fas', 'up-right-from-square']" />
                                </a>
                            </dd>
                        </div>
                        <div>
                            <dt>Time zone</dt>
                            <dd>{{ selected.time_zone || "—" }}</dd>
                        </div>
                    </dl>
                    <div v-if="mapTiles" class="mini-kaart" role="img" :aria-label="`Map of ${selected.name}`">
                        <div class="mk-grid" :style="{ left: mapTiles.gridLeft, top: mapTiles.gridTop }">
                            <img
                                v-for="(t, i) in mapTiles.tiles"
                                :key="i"
                                :src="t.src"
                                alt=""
                                loading="lazy"
                                :style="{ left: `${t.left}px`, top: `${t.top}px` }"
                            >
                        </div>
                        <span class="mk-pin" aria-hidden="true"></span>
                        <a class="mk-attr" :href="mapTiles.osm" target="_blank" rel="noopener">&copy; Esri</a>
                    </div>
                </div>

                <!-- Opening hours -->
                <div class="paneel">
                    <div class="paneel-kop">
                        <h2>Opening hours</h2>
                        <router-link
                            to="/opening-hours"
                            class="btn btn-stil btn-icoon btn-sm rechts"
                            title="Edit opening hours"
                            aria-label="Edit opening hours"
                        >
                            <font-awesome-icon :icon="['fas', 'pen']" />
                        </router-link>
                    </div>
                    <p v-if="loadingHours" class="hint">Loading hours...</p>
                    <table v-else class="tabel uren">
                        <tbody>
                            <tr v-for="d in week" :key="d.day" :class="{ 'vandaag-rij': d.isToday }">
                                <td>
                                    {{ d.short }}
                                    <span v-if="d.isToday" class="badge badge-stil">Today</span>
                                </td>
                                <td class="num rechts">
                                    <div class="uren-regel">
                                        <span v-if="d.status === 'open'">{{ d.from }} – {{ d.till }}</span>
                                        <span v-else-if="d.status === 'varies'" class="leegwaarde">Varies</span>
                                        <span v-else class="leegwaarde">Closed</span>
                                        <span v-if="d.exception" class="badge" :class="d.exClass">{{ d.exLabel }}</span>
                                    </div>
                                    <div v-if="d.exception" class="normaliter">Normally {{ d.normalText }}</div>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <!-- People -->
                <div class="paneel">
                    <div class="paneel-kop"><h2>People</h2></div>
                    <dl class="dl">
                        <div>
                            <dt>Operators</dt>
                            <dd>
                                <div v-if="operators.length" class="mensen">
                                    <span v-for="u in operators" :key="u.id">
                                        {{ u.username }}
                                        <span v-if="u.isAdmin" class="muted">(admin)</span>
                                    </span>
                                </div>
                                <span v-else class="leegwaarde">Managed by X-Cube staff</span>
                            </dd>
                        </div>
                        <div>
                            <dt>Contacts</dt>
                            <dd>
                                <span v-if="contactsError" class="leegwaarde">Unavailable</span>
                                <span v-else>{{ contactsCount }} · {{ monitoringCount }} with monitoring</span>
                            </dd>
                        </div>
                    </dl>
                    <div class="knoprij">
                        <router-link to="/contacts" class="btn btn-stil btn-sm">Contacts</router-link>
                        <router-link to="/location" class="btn btn-stil btn-sm">Users</router-link>
                    </div>
                </div>
            </section>

            <section class="rij rij-2">
                <!-- Devices -->
                <div class="paneel">
                    <div class="paneel-kop"><h2>Devices</h2></div>
                    <div class="tabel-wikkel">
                        <table class="tabel">
                            <thead>
                                <tr><th>Device</th><th>Type</th><th>Status</th></tr>
                            </thead>
                            <tbody>
                                <tr v-for="d in devices" :key="d.code">
                                    <td class="num nowrap">{{ d.code }}</td>
                                    <td>{{ d.type }}</td>
                                    <td>
                                        <span
                                            v-if="d.status"
                                            class="badge"
                                            :class="statusClass(d.status)"
                                            :title="d.issues.length ? d.issues.map((i) => i.label).join(', ') : ''"
                                        >{{ statusLabel(d.status) }}</span>
                                        <span v-else class="leegwaarde">—</span>
                                    </td>
                                </tr>
                                <tr v-if="devices.length === 0">
                                    <td colspan="3" class="leegwaarde">No devices at this location.</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <p v-if="statusError" class="hint">Live status is not available here; showing the location's devices only.</p>
                    <p v-else class="hint">A running game or live detail is not shown here.</p>
                    <div class="knoprij">
                        <a class="btn btn-stil btn-sm" href="https://control.x-cube.nl" target="_blank" rel="noopener">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M14 4h6v6M20 4l-9 9M18 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h5" /></svg>
                            More details on control.x-cube.nl
                        </a>
                    </div>
                </div>

                <!-- Prices -->
                <div class="paneel">
                    <div class="paneel-kop">
                        <div class="tekst">
                            <h2>Prices</h2>
                            <span class="uitleg">Per player, including VAT. The price follows the game's duration.</span>
                        </div>
                    </div>
                    <p v-if="pricesError === 'role'" class="hint">Pricing is not available for this role.</p>
                    <p v-else-if="pricesError" class="hint">Could not load prices.</p>
                    <div v-else class="tabel-wikkel">
                        <table class="tabel">
                            <thead>
                                <tr><th>Duration</th><th class="rechts">Price</th></tr>
                            </thead>
                            <tbody>
                                <tr v-for="p in priceRows" :key="p.id">
                                    <td>{{ p.duration }} min</td>
                                    <td class="num rechts">{{ money(p) }}</td>
                                </tr>
                                <tr v-if="priceRows.length === 0">
                                    <td colspan="2" class="leegwaarde">No prices set.</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <p class="hint">
                        Prices are set by X-Corp and cannot be changed here. To adjust a rate, email
                        <a href="mailto:info@x-corp.nl">info@x-corp.nl</a>.
                    </p>
                </div>
            </section>
        </template>
        </div>
    </div>
</template>

<script setup>
import { computed, onMounted, ref, watch } from "vue";
import { listContacts } from "../../api/contacts";
import { getLocationUsers, getUserLocationsSimple } from "../../api/graphql/locations";
import { getContentBlocks, getLocationSchedule, getPrices, PricesAccessError } from "../../api/rest";
import { getXcubeStatus } from "../../api/xcubeStatus";
import {
    WEEKDAYS,
    calendarDateOf,
    hhmmToTime,
    weekScheduleForDate,
} from "../../helpers/openingHours";
import RenderContentBlocks from "../content-block/RenderContentBlocks.vue";

const DEVICE_TYPE = {
    "x-cube": "X-Cube",
    "x-pod": "X-Pod",
    "x-kiosk": "X-Kiosk",
    "x-tend": "X-Tend",
};

const locations = ref([]);
const loadingLocations = ref(true);
const selectedId = ref(null);

const contentBlocks = ref([]);
const schedule = ref({ rows: [], overrides: [], closures: [] });
const loadingHours = ref(false);
const prices = ref([]);
const pricesError = ref("");
const locationUsers = ref([]);
const statusCubes = ref([]);
const statusError = ref(false);
const contactsCount = ref(0);
const monitoringCount = ref(0);
const contactsError = ref(false);

const selected = computed(
    () => locations.value.find((l) => String(l.id) === String(selectedId.value)) || null,
);

const addressLine1 = computed(() =>
    [selected.value?.address, selected.value?.zip_code].filter(Boolean).join(", "),
);
const addressLine2 = computed(() =>
    [selected.value?.place_name, selected.value?.country].filter(Boolean).join(", "),
);

// Strapi stores "-" as a placeholder for "no phone"; treat it as empty.
const phone = computed(() => {
    const p = (selected.value?.phone_number || "").trim();
    return p && p !== "-" ? p : "";
});

const todayIndex = computed(() => (new Date().getDay() + 6) % 7);

// Monday-first calendar dates of the current week, as YYYY-MM-DD in the
// viewer's local zone (same basis as todayIndex).
const weekDates = computed(() => {
    const now = new Date();
    const monday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - todayIndex.value);
    return WEEKDAYS.map((_, i) =>
        calendarDateOf(new Date(monday.getFullYear(), monday.getMonth(), monday.getDate() + i)),
    );
});

const EX_LABEL = {
    extra: "Extra opening",
    tempClosed: "Temporarily closed",
    partialClosed: "Partly closed",
};
const EX_CLASS = {
    extra: "badge-goed",
    tempClosed: "badge-fout",
    partialClosed: "badge-letop",
};

const week = computed(() => {
    const tz = selected.value?.time_zone || "UTC";
    return WEEKDAYS.map((day, i) => {
        const info = weekScheduleForDate(schedule.value, weekDates.value[i], tz);
        const normalText = info.normal.open
            ? `${hhmmToTime(info.normal.from)} – ${hhmmToTime(info.normal.till)}`
            : "closed";
        return {
            day,
            short: day.slice(0, 3),
            isToday: i === todayIndex.value,
            status: info.status,
            from: info.status === "open" ? hhmmToTime(info.from) : "",
            till: info.status === "open" ? hhmmToTime(info.till) : "",
            exception: info.exception,
            exLabel: info.exception ? EX_LABEL[info.exception] : "",
            exClass: info.exception ? EX_CLASS[info.exception] : "",
            normalText,
        };
    });
});

const priceRows = computed(() =>
    prices.value
        .filter((p) => p.price_per_player && p.amount_in_cents != null && p.location == null)
        .sort((a, b) => a.duration - b.duration),
);
const money = (p) =>
    new Intl.NumberFormat("nl-NL", { style: "currency", currency: p.currency || "EUR" }).format(
        p.amount_in_cents / 100,
    );

const operators = computed(() =>
    locationUsers.value
        .filter((u) => (u.role?.type || "").startsWith("x_cube_operator"))
        .map((u) => ({
            id: u.id,
            username: u.username,
            isAdmin: u.role?.type === "x_cube_operator_admin",
        })),
);

const devices = computed(() => {
    const byId = {};
    const byCode = {};
    for (const c of statusCubes.value) {
        // The status service echoes the Strapi xcube id (`xcubeId`); match on that
        // rather than the display code, which the Hub builds as "name letter" while
        // the service keys by name only (so any lettered cube would miss).
        if (c.xcubeId != null) byId[String(c.xcubeId)] = c;
        if (c.code) byCode[c.code] = c;
    }
    const xcubes = selected.value?.xcubes || [];
    return xcubes.map((x) => {
        const code = `${x.name || ""}${x.letter ? ` ${x.letter}` : ""}`.trim();
        const st = byId[String(x.id)] || byCode[code];
        return {
            code: code || x.name || String(x.id),
            type: DEVICE_TYPE[x.type] || x.type || "X-Cube",
            status: st?.status || null,
            issues: st?.issues || [],
        };
    });
});

const statusClass = (s) =>
    s === "ok" ? "badge-goed" : s === "attention" ? "badge-letop" : "badge-fout";
const statusLabel = (s) => (s === "ok" ? "OK" : s === "attention" ? "Attention" : "Critical");

const mapTiles = computed(() => {
    const lat = Number(selected.value?.latitude);
    const lng = Number(selected.value?.longitude);
    if (!Number.isFinite(lat) || !Number.isFinite(lng) || (lat === 0 && lng === 0)) return null;
    const z = 13;
    const size = 256;
    const n = 2 ** z;
    const gx = ((lng + 180) / 360) * n * size;
    const gy = ((1 - Math.asinh(Math.tan((lat * Math.PI) / 180)) / Math.PI) / 2) * n * size;
    const tx0 = Math.floor(gx / size) - 1;
    const ty0 = Math.floor(gy / size) - 1;
    const mx = gx - tx0 * size;
    const my = gy - ty0 * size;
    const tiles = [];
    for (let j = 0; j < 3; j++) {
        for (let i = 0; i < 3; i++) {
            tiles.push({
                src: `https://server.arcgisonline.com/ArcGIS/rest/services/World_Street_Map/MapServer/tile/${z}/${ty0 + j}/${tx0 + i}`,
                left: i * size,
                top: j * size,
            });
        }
    }
    return {
        tiles,
        gridLeft: `calc(50% - ${mx}px)`,
        gridTop: `${80 - my}px`,
        osm: `https://www.openstreetmap.org/#map=${z}/${lat}/${lng}`,
    };
});

const loadLocationData = async (location) => {
    if (!location) return;
    const id = location.id;

    // Each source is independent; one failing must not blank the whole page.
    contentBlocks.value = await getContentBlocks("operator-info", id).catch(() => []);

    loadingHours.value = true;
    schedule.value = await getLocationSchedule(id).catch(() => ({
        rows: [],
        overrides: [],
        closures: [],
    }));
    loadingHours.value = false;

    locationUsers.value = await getLocationUsers(id).catch(() => []);

    try {
        const res = await getXcubeStatus(id);
        statusCubes.value = res.cubes || [];
        statusError.value = false;
    } catch {
        statusCubes.value = [];
        statusError.value = true;
    }

    try {
        const contacts = await listContacts({ locationId: id });
        const rows =
            contacts.contacts || contacts.data || (Array.isArray(contacts) ? contacts : []);
        contactsCount.value = rows.length;
        monitoringCount.value = rows.filter((c) => c.receive_monitoring).length;
        contactsError.value = false;
    } catch {
        contactsError.value = true;
    }
};

watch(selectedId, () => loadLocationData(selected.value));

onMounted(async () => {
    const [locs, priceRowsResult] = await Promise.all([
        getUserLocationsSimple(),
        getPrices().catch((error) => {
            pricesError.value = error instanceof PricesAccessError ? "role" : "load";
            return [];
        }),
    ]);
    locations.value = locs;
    prices.value = priceRowsResult;
    loadingLocations.value = false;
    if (locs.length > 0) {
        selectedId.value = locs[0].id;
        await loadLocationData(locs[0]);
    }
});
</script>
